import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useConfirm } from '../../components/shared/Confirm';
import { useIsMobile } from '../../utils/useIsMobile';

// Lo que trae el plan pase lo que pase. No son columnas de `planes`: son las
// funciones que el sistema nunca apaga, asi que van fijas y sin casilla. Todo
// lo demas se cobra aparte y se enciende en el arbol de permisos.
const INCLUIDO = [
  ['Clientes',                    'Ficha e historial de cada cliente'],
  ['Servicios',                   'Catálogo con precios y duración'],
  ['Página de reservas',          'Link público para que el cliente agende solo'],
  ['Confirmaciones por WhatsApp', 'Aviso automático al agendar'],
  ['Soporte técnico',             'Tickets y respuesta por WhatsApp'],
];

// Módulos que el plan puede traer o no. Son columnas reales de `planes` y la
// misma lista que usa el árbol de permisos: si el plan lo trae, el negocio lo
// tiene sin pagarlo aparte; si no, se le enciende y se le cobra.
const MODULOS_PLAN = [
  ['tiene_calendario',        'Calendario',         'Agenda del día y de la semana'],
  ['tiene_citas',             'Citas',              'Listado, cambios y cancelaciones'],
  ['tiene_horarios',          'Horarios',           'Horario de atención y descansos'],
  ['tiene_mi_negocio',        'Mi Negocio',         'Datos, logo y página pública'],
  ['tiene_notificaciones',    'Notificaciones',     'Plantillas y avisos por WhatsApp'],
  ['tiene_trabajadores',      'Trabajadores',       'Colaboradores con agenda propia'],
  ['tiene_multi_sucursal',    'Sedes',              'Varias sedes por negocio'],
  ['tiene_finanzas',          'Finanzas',           'Ingresos, gastos y utilidad'],
  ['tiene_reportes',          'Reportes operativos','Citas, servicios y ocupación'],
  ['tiene_reportes_avanzados','Reportes avanzados', 'Comparativos y exportación'],
  ['tiene_dashboard',         'Dashboard',          'Indicadores del día'],
];

export default function PlansPage() {
  const esMovil = useIsMobile(900);
  const [plans, setPlans]       = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState({});
  const [saving, setSaving]     = useState(false);
  const [toggling, setToggling] = useState(null); // id del plan en proceso
  const [success, setSuccess]   = useState('');
  const [error, setError]       = useState('');
  const confirmar = useConfirm();
  const [tab, setTab] = useState('planes');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/plans/todos');
      setPlans(res.data);
    } catch { setError('Error cargando planes'); }
    finally { setLoading(false); }

    // Solo para el contador de la derecha: si falla, la tarjeta no se muestra
    // y la pagina sigue funcionando igual.
    try {
      const n = await api.get('/businesses');
      setNegocios(n.data || []);
    } catch { /* sin contador */ }
  };

  const notify = (msg, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(''), 4000); }
    else          { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); }
  };

  const startEdit = (plan) => { setEditing(plan.id); setForm(plan); };

  /**
   * Crea un plan vacío y lo deja abierto en edición.
   *
   * Se crea primero en el servidor en vez de abrir un formulario aparte: el
   * plan nace inactivo, así que no hay riesgo en que exista mientras se
   * termina de configurar, y se reutiliza el mismo editor de siempre.
   */
  const crearPlan = async () => {
    setSaving(true);
    try {
      const r = await api.post('/plans', {
        nombre_visible: 'Plan nuevo', precio: 0, max_usuarios: 1, max_sucursales: 1,
      });
      notify('Plan creado. Configúralo y actívalo cuando esté listo.');
      const res = await api.get('/plans/todos');
      setPlans(res.data);
      const creado = res.data.find(p => p.id === r.data.id);
      if (creado) startEdit(creado);
    } catch (err) {
      notify(err.response?.data?.error || 'No se pudo crear el plan', true);
    } finally { setSaving(false); }
  };

  const eliminarPlan = async (plan) => {
    if (!await confirmar({
      titulo: 'Eliminar plan',
      mensaje: `¿Eliminar "${plan.nombre_visible}"?`,
      detalle: 'Esta acción no se puede deshacer. Solo se puede eliminar un plan que ningún negocio esté usando.',
      aceptar: 'Eliminar', peligro: true,
    })) return;
    try {
      await api.delete(`/plans/${plan.id}`);
      notify('Plan eliminado');
      load();
    } catch (err) {
      notify(err.response?.data?.error || 'No se pudo eliminar el plan', true);
    }
  };

  const save = async () => {
    // Los campos numericos viajan como texto para poder vaciarlos mientras se
    // escribe; aqui se convierten y se validan antes de mandarlos.
    const nombre = String(form.nombre_visible || '').trim();
    const precio = Number(form.precio);
    if (!nombre) return notify('Escribe el nombre del plan', true);
    if (!Number.isFinite(precio) || precio < 0) return notify('El precio no puede ser negativo', true);

    setSaving(true);
    try {
      await api.put(`/plans/${editing}`, {
        ...form,
        nombre_visible: nombre.slice(0, 80),
        precio,
        max_usuarios:   Math.max(1, Number(form.max_usuarios)   || 1),
        max_sucursales: Math.max(1, Number(form.max_sucursales) || 1),
      });
      notify('Plan actualizado correctamente');
      setEditing(null);
      load();
    } catch (err) {
      notify(err.response?.data?.error || 'Error guardando plan', true);
    }
    finally { setSaving(false); }
  };

  const togglePlan = async (plan) => {
    const accion = plan.activo ? 'desactivar' : 'activar';
    if (!await confirmar({
      titulo: plan.activo ? 'Desactivar plan' : 'Activar plan',
      mensaje: `¿Confirmas ${accion} el plan "${plan.nombre_visible}"?`,
      detalle: `Todos los usuarios de negocios con este plan también serán ${plan.activo ? 'desactivados' : 'activados'}.`,
      aceptar: plan.activo ? 'Desactivar' : 'Activar',
      peligro: !!plan.activo,
    })) return;

    setToggling(plan.id);
    try {
      const res = await api.patch(`/plans/${plan.id}/toggle`);
      notify(res.data.message);
      load();
    } catch (err) {
      notify(err.response?.data?.error || 'Error al cambiar estado del plan', true);
    } finally { setToggling(null); }
  };

  const planColor  = (name) => ({ basic: '#6366f1', medium: '#3b82f6', premium: '#4f46e5' }[name?.toLowerCase()] || '#888');
  const planIcon   = (name) => ({ basic: '🟢', medium: '🔵', premium: '🟣' }[name?.toLowerCase()] || '⚙️');

  const boolField = (key, label) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
      <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{label}</span>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!form[key]}
          onChange={e => setForm({ ...form, [key]: e.target.checked })}
        />
        <span style={{ fontSize: '0.8rem', color: form[key] ? 'var(--color-gold)' : 'var(--color-text-muted)' }}>
          {form[key] ? '✅ Activo' : '❌ Inactivo'}
        </span>
      </label>
    </div>
  );

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Plan y servicios</h1>
          <p className="page-subtitle">Un solo plan; lo demás se activa por negocio y se suma a su mensualidad</p>
        </div>
        {tab === 'planes' && (
          <button className="btn btn-primary" onClick={crearPlan} disabled={saving}>
            + Nuevo plan
          </button>
        )}
      </div>

      {/* Pestañas: los precios de los servicios adicionales viven aquí, pero
          se asignan en el árbol de permisos. */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: '1.5rem', flexWrap: 'wrap',
        borderBottom: '1px solid var(--color-border)',
      }}>
        {[['planes', 'El plan'], ['servicios', 'Servicios']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
            padding: '0.6rem 1rem', fontSize: '0.9rem',
            fontWeight: tab === id ? 700 : 500,
            color: tab === id ? 'var(--color-gold)' : 'var(--color-text-muted)',
            borderBottom: `2px solid ${tab === id ? 'var(--color-gold)' : 'transparent'}`,
            marginBottom: -1,
          }}>{label}</button>
        ))}
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{success}</div>}
      {error   && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      {tab === 'servicios' && <ServiciosAdicionales notify={notify} />}

      {tab === 'planes' && (loading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Cargando...</p>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: esMovil ? '1fr' : 'minmax(0, 1fr) 320px',
          gap: '1.25rem', alignItems: 'start',
        }}>
        <div style={{ display: 'grid', gap: '1.25rem' }}>
          {plans.map(plan => {
            const inactivo = !plan.activo;
            const usan = negocios.filter(n => String(n.plan_id) === String(plan.id)).length;
            return (
              <div
                key={plan.id}
                className="card"
                style={{
                  padding: 0, overflow: 'hidden',
                  borderTop: `3px solid ${inactivo ? '#94a3b8' : planColor(plan.nombre)}`,
                  opacity: inactivo ? 0.7 : 1,
                }}
              >
                {/* Encabezado: nombre, precio y estado, como en el boceto */}
                <div style={{ padding: '1.25rem 1.4rem', borderBottom: '1px solid var(--color-border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.15rem', color: inactivo ? '#94a3b8' : planColor(plan.nombre), fontFamily: 'var(--font-display)' }}>
                        {planIcon(plan.nombre)} {plan.nombre_visible}
                      </h3>
                      <div style={{ marginTop: '2px', fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1, color: 'var(--color-text)' }}>
                        ${plan.precio?.toLocaleString('es-CO')}
                        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', fontWeight: 400, letterSpacing: 0 }}>/mes</span>
                      </div>
                      <div style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                        Hasta {plan.max_usuarios} usuario{plan.max_usuarios === 1 ? '' : 's'} · {plan.max_sucursales} sede{plan.max_sucursales === 1 ? '' : 's'}
                        {usan > 0 && <> · <strong style={{ color: 'var(--color-text)' }}>{usan}</strong> negocio{usan === 1 ? '' : 's'}</>}
                      </div>
                    </div>

                    <span style={{
                      fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', padding: '3px 9px', borderRadius: 99,
                      marginLeft: 'auto', alignSelf: 'center',
                      background: inactivo ? '#fee2e2' : '#dcfce7',
                      color:      inactivo ? '#b91c1c' : '#15803d',
                    }}>{inactivo ? 'Inactivo' : 'Activo'}</span>

                    {editing !== plan.id && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', alignSelf: 'center' }}>
                        <button className="btn btn-secondary" style={{ fontSize: '0.8rem' }} onClick={() => startEdit(plan)}>
                          Editar
                        </button>
                        <button
                          onClick={() => togglePlan(plan)}
                          disabled={toggling === plan.id}
                          style={{
                            fontSize: '0.75rem', fontWeight: 600, padding: '4px 12px',
                            border: 'none', borderRadius: 6, cursor: 'pointer',
                            background: inactivo ? '#dcfce7' : '#fee2e2',
                            color:      inactivo ? '#15803d' : '#b91c1c',
                          }}
                        >
                          {toggling === plan.id ? '...' : inactivo ? '▶ Activar' : '⏸ Desactivar'}
                        </button>
                        {/* Eliminar solo se ofrece en planes inactivos: si
                            está activo lo normal es desactivarlo, no borrarlo,
                            y el servidor rechaza borrar uno que esté en uso. */}
                        {inactivo && (
                          <button className="btn btn-ghost" style={{ fontSize: '0.78rem', color: 'var(--color-error)' }}
                                  onClick={() => eliminarPlan(plan)}>
                            🗑 Eliminar
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Formulario de edición */}
                {editing === plan.id ? (
                  <div style={{ padding: '1.5rem' }}>
                    <div className="form-group">
                      <label className="form-label">Nombre del plan</label>
                      <input
                        type="text" className="form-input" maxLength={80}
                        value={form.nombre_visible ?? ''}
                        onChange={e => setForm({ ...form, nombre_visible: e.target.value })}
                      />
                      {/* El nombre interno no se toca: de el salen el color y
                          el icono, y cambiarlo desligaria al plan de sus
                          negocios. Lo que se edita es el visible. */}
                      <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', marginTop: 4 }}>
                        Así lo ve el negocio. La clave interna (<code>{plan.nombre}</code>) no cambia.
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Precio mensual</label>
                      {/* Cadena y no numero: con `+e.target.value` no se podia
                          borrar el campo para escribir otra cifra. */}
                      <input
                        type="number" min="0" step="100" className="form-input"
                        value={form.precio ?? ''}
                        onChange={e => setForm({ ...form, precio: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Máximo de usuarios</label>
                      <input type="number" min="1" className="form-input" value={form.max_usuarios ?? ''} onChange={e => setForm({ ...form, max_usuarios: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Máximo de sedes</label>
                      <input type="number" min="1" className="form-input" value={form.max_sucursales ?? ''} onChange={e => setForm({ ...form, max_sucursales: e.target.value })} />
                    </div>
                    <div style={{ marginTop: '1rem' }}>
                      <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>MÓDULOS</div>
                      {MODULOS_PLAN.map(([campo, nombre]) => (
                        <React.Fragment key={campo}>{boolField(campo, nombre)}</React.Fragment>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                      <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancelar</button>
                      <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
                    </div>
                  </div>
                ) : (
                  /* Lo que trae el plan: lo fijo, más los módulos que este
                     plan ya incluya y que por tanto no se cobran aparte. */
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.9rem 1.4rem 0.6rem', fontWeight: 600, fontSize: '0.92rem' }}>
                      <span>⭐</span> Incluido en el plan
                    </div>
                    {INCLUIDO.map(([nombre, desc]) => (
                      <div key={nombre} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        padding: '0.62rem 1.4rem', borderTop: '1px solid var(--color-border)', fontSize: '0.87rem',
                      }}>
                        <span style={{ color: 'var(--color-success)', fontWeight: 700 }}>✓</span>
                        <span>
                          <strong style={{ fontWeight: 600 }}>{nombre}</strong>
                          <div style={{ fontSize: '0.77rem', color: 'var(--color-text-muted)' }}>{desc}</div>
                        </span>
                      </div>
                    ))}

                    {MODULOS_PLAN.filter(([campo]) => plan[campo]).map(([campo, nombre, desc]) => (
                      <div key={campo} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        padding: '0.62rem 1.4rem', borderTop: '1px solid var(--color-border)', fontSize: '0.87rem',
                      }}>
                        <span style={{ color: 'var(--color-success)', fontWeight: 700 }}>✓</span>
                        <span>
                          <strong style={{ fontWeight: 600 }}>{nombre}</strong>
                          <div style={{ fontSize: '0.77rem', color: 'var(--color-text-muted)' }}>
                            {desc} · no se cobra aparte en este plan
                          </div>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Columna lateral: explica el modelo y cuántos negocios hay. */}
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div className="card" style={{ padding: '1.1rem 1.25rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>Un solo plan</h3>
            <p style={{ margin: '6px 0 0', fontSize: '0.82rem', color: 'var(--color-text-muted)', lineHeight: 1.55 }}>
              El barbero no elige entre básico, medio y premium. Entra con lo esencial y se le
              activa lo que necesite, cuando lo necesite, desde el <strong>Árbol de permisos</strong>.
            </p>
          </div>

          <div className="card" style={{ padding: '1.1rem 1.25rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>Negocios activos</h3>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
              <span style={{ fontSize: '1.9rem', fontWeight: 700, letterSpacing: '-0.03em' }}>{negocios.length}</span>
              <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>en la plataforma</span>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: '0.82rem', color: 'var(--color-text-muted)', lineHeight: 1.55 }}>
              Cada uno paga su plan más los módulos que tenga encendidos.
            </p>
          </div>

          <div className="alert alert-info">
            <span>💡</span>
            <div>
              Los precios de los módulos se editan en <strong>Servicios</strong>; encenderlos para
              un negocio se hace en el <strong>Árbol de permisos</strong>.
            </div>
          </div>
        </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Precio de cada servicio adicional.
 *
 * Solo se definen los importes. Encender un servicio para un negocio se sigue
 * haciendo en el árbol de permisos, que es donde ya se decide el acceso: así
 * dar acceso y cobrarlo son la misma acción y no dos listas que se separan.
 */
function ServiciosAdicionales({ notify }) {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState('');
  const [borrador, setBorrador] = useState({});   // { modulo: '19900' }

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await api.get('/plans/servicios');
      setItems(r.data);
      setBorrador(Object.fromEntries(r.data.map(s => [s.modulo, String(s.precio)])));
      setFallo('');
    } catch (err) {
      setFallo(err.response?.status === 404
        ? 'El servidor todavía no tiene este módulo: falta subir routes/plans.js y reiniciar.'
        : (err.response?.data?.error || 'No se pudo cargar el catálogo'));
    } finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, []);

  const guardar = async (s) => {
    const precio = Number(borrador[s.modulo]);
    if (!Number.isFinite(precio) || precio < 0) return notify('El precio no puede ser negativo', true);
    if (precio === s.precio) return;
    try {
      const r = await api.put(`/plans/servicios/${s.modulo}`, { precio });
      notify(r.data?.message || 'Precio actualizado');
      cargar();
    } catch (err) {
      notify(err.response?.data?.error || 'No se pudo guardar', true);
    }
  };

  if (cargando) return <p style={{ color: 'var(--color-text-muted)' }}>Cargando servicios...</p>;
  if (fallo) {
    return (
      <div className="card" style={{ padding: '2.5rem 2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>⚠️</div>
        <p style={{ margin: '0 auto', maxWidth: '54ch', fontSize: '0.86rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>{fallo}</p>
        <button className="btn btn-secondary" style={{ marginTop: 14 }} onClick={cargar}>Reintentar</button>
      </div>
    );
  }

  return (
    <>
      <div className="card" style={{ padding: '1.1rem 1.3rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Precio de cada servicio</h3>
        <p style={{ margin: '4px 0 14px', fontSize: '0.83rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          Lo que se cobra al mes por tener el módulo encendido. Se asignan en el{' '}
          <strong>Árbol de permisos</strong>, y allí se suman a la mensualidad del negocio.
        </p>

        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%', minWidth: 520 }}>
            <thead>
              <tr>
                <th>Servicio</th>
                <th style={{ textAlign: 'right', width: 150 }}>Precio al mes</th>
                <th style={{ textAlign: 'right', width: 110 }}>Lo tienen</th>
              </tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={s.modulo}>
                  <td style={{ fontSize: '0.86rem' }}>
                    <div style={{ fontWeight: 500 }}>{s.nombre}</div>
                    {s.descripcion && (
                      <div style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>{s.descripcion}</div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <input
                      type="number" min="0" step="100" className="form-input"
                      style={{ margin: 0, textAlign: 'right', maxWidth: 130, display: 'inline-block' }}
                      value={borrador[s.modulo] ?? ''}
                      onChange={e => setBorrador(b => ({ ...b, [s.modulo]: e.target.value }))}
                      onBlur={() => guardar(s)}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    />
                  </td>
                  <td style={{ textAlign: 'right', fontSize: '0.84rem', color: 'var(--color-text-muted)' }}>
                    {s.negocios ? <><strong style={{ color: 'var(--color-text)' }}>{s.negocios}</strong> negocio{s.negocios === 1 ? '' : 's'}</> : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="alert alert-info" style={{ marginTop: '1rem' }}>
        <span>💡</span>
        <div>
          El precio <strong>se congela al asignarlo</strong>. Si mañana subes una tarifa, los
          negocios que ya tienen ese servicio conservan la suya y solo los nuevos pagan la nueva.
        </div>
      </div>
    </>
  );
}
